<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">point_of_sale</span>
      <h2>Caisse</h2>
    </div>

    <p class="section-intro">
      Le module Caisse gère les encaissements des bons de commande et les opérations
      sur les portefeuilles clients (crédits, remboursements). Plusieurs modes de paiement
      sont disponibles : Portefeuille, Espèces, Chèque, Virement, Crédit.
    </p>

    <!-- ===== PAIEMENT D'UNE COMMANDE ===== -->
    <h3 class="sub-title">Encaisser un bon de commande</h3>
    <p class="section-text">
      Lorsqu'un bon de commande est envoyé à la caisse, il apparaît dans la liste.
      Cliquez dessus pour procéder au paiement.
    </p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Client</strong></td><td>Texte (désactivé)</td><td>—</td><td>Affiché automatiquement depuis le bon de commande.</td></tr>
          <tr><td><strong>Bon N°</strong></td><td>Texte (désactivé)</td><td>—</td><td>Numéro du bon de commande.</td></tr>
          <tr><td><strong>Total</strong></td><td>Nombre (désactivé)</td><td>—</td><td>Montant total à payer.</td></tr>
          <tr><td><strong>Mode de paiement</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Choisir parmi : <em>Portefeuille</em>, <em>Espèces</em>, <em>Chèque</em>, <em>Virement</em>, <em>Crédit</em>.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Champs conditionnels selon le mode de paiement</h4>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Mode</th><th>Champs supplémentaires</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Portefeuille</strong></td>
            <td><em>Solde</em> (affiché automatiquement, désactivé) — Le montant est déduit du portefeuille du client.</td>
          </tr>
          <tr>
            <td><strong>Crédit</strong></td>
            <td><em>Solde crédit</em> (affiché automatiquement, désactivé) — Le montant est mis en créance. Le client paiera plus tard.</td>
          </tr>
          <tr>
            <td><strong>Espèces</strong></td>
            <td><em>Montant</em> (nombre, obligatoire) — Saisissez le montant reçu en espèces.</td>
          </tr>
          <tr>
            <td><strong>Chèque</strong></td>
            <td><em>Montant</em>, <em>N° de chèque</em>, <em>Banque émettrice</em> (tous obligatoires).</td>
          </tr>
          <tr>
            <td><strong>Virement</strong></td>
            <td><em>Montant</em>, <em>N° de virement</em>, <em>Banque</em> (tous obligatoires).</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Encaisser un bon</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Caisse</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>La liste des bons en attente de paiement s'affiche. Cliquez sur un bon.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Les champs <em>Client</em>, <em>Bon N°</em> et <em>Total</em> sont affichés automatiquement (non modifiables).</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Sélectionnez le <em>Mode de paiement</em> dans la liste déroulante.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Selon le mode choisi, remplissez les champs supplémentaires :</p>
        <ul class="doc-list">
          <li><strong>Portefeuille :</strong> Vérifiez que le solde est suffisant.</li>
          <li><strong>Espèces :</strong> Saisissez le <em>Montant</em> reçu.</li>
          <li><strong>Chèque :</strong> Saisissez <em>Montant</em>, <em>N° de chèque</em> et <em>Banque</em>.</li>
          <li><strong>Virement :</strong> Saisissez <em>Montant</em>, <em>N° de virement</em> et <em>Banque</em>.</li>
          <li><strong>Crédit :</strong> Le montant est porté en créance.</li>
        </ul>
      </div>
      <div class="step-item">
        <div class="step-num">6</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le paiement est enregistré et le bon passe en statut « Payé ».</p>
      </div>
    </div>

    <!-- ===== CRÉDITER PORTEFEUILLE ===== -->
    <h3 class="sub-title">Créditer le portefeuille d'un client</h3>
    <p class="section-text">
      Permet d'ajouter un crédit au portefeuille d'un client (avance, remboursement, etc.).
    </p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Client</strong></td><td>Texte (désactivé)</td><td>—</td><td>Nom du client (pré-rempli).</td></tr>
          <tr><td><strong>Solde actuel</strong></td><td>Nombre (désactivé)</td><td>—</td><td>Solde actuel du portefeuille.</td></tr>
          <tr><td><strong>Montant</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Montant à créditer.</td></tr>
          <tr><td><strong>Motif</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Raison du crédit (avance, remboursement, etc.).</td></tr>
          <tr><td><strong>Mode de paiement</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Espèces, Chèque ou Virement.</td></tr>
          <tr><td><strong>N° chèque/virement</strong></td><td>Texte</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Uniquement pour Chèque ou Virement.</td></tr>
          <tr><td><strong>Banque</strong></td><td>Texte</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Uniquement pour Chèque ou Virement.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Dans le module <strong>Caisse</strong>, trouvez le client et cliquez sur l'icône de portefeuille.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Saisissez le <em>Montant</em> et le <em>Motif</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Mode de paiement</em>. Si chèque ou virement, remplissez le numéro et la banque.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le portefeuille du client est immédiatement crédité.</p>
      </div>
    </div>

    <div class="info-card info">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#5b9bd5">info</span>
        Information
      </div>
      <p>
        L'historique de tous les encaissements et crédits est visible dans le module <strong>Comptabilité</strong>.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocCaisse" };
</script>
